import React, { useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { apiService } from '../../../../services/api';

interface AttachmentModalProps {
    showAttachmentModal: boolean;
    setShowAttachmentModal: (show: boolean) => void;
    opportunityId: number;
    onAttachmentCreated?: () => void;
}

interface UploadedFile {
    file: File;
    size: string;
    name: string;
}

export const AttachmentModal: React.FC<AttachmentModalProps> = ({
    showAttachmentModal,
    setShowAttachmentModal,
    opportunityId,
    onAttachmentCreated
}) => {
    const [attachmentTitle, setAttachmentTitle] = useState('');
    const [attachmentDescription, setAttachmentDescription] = useState('');
    const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files[0]) {
            setUploadedFile({
                file: files[0],
                size: formatFileSize(files[0].size),
                name: files[0].name
            });
        }
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const resetForm = () => {
        setAttachmentTitle('');
        setAttachmentDescription('');
        setUploadedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleClose = () => {
        resetForm();
        setShowAttachmentModal(false);
    };

    const handleSaveAttachment = async () => {
        if (!attachmentTitle.trim()) {
            alert('O título é obrigatório');
            return;
        }

        if (!uploadedFile) {
            alert('É necessário enviar um arquivo');
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append('id_oportunidade', opportunityId.toString());
            formData.append('titulo', attachmentTitle);
            formData.append('descricao', attachmentDescription);
            formData.append('arquivo', uploadedFile.file);

            await apiService.createAnexo({
                id_oportunidade: opportunityId,
                titulo: attachmentTitle,
                descricao: attachmentDescription,
                arquivo: uploadedFile.file
            });

            setShowSuccessModal(true);
            resetForm();
            onAttachmentCreated?.();
        } catch (error) {
            console.error('Erro ao salvar anexo:', error);
            alert('Erro ao salvar o anexo. Por favor, tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Modal 
                show={showAttachmentModal} 
                onHide={handleClose}
                centered
                className="custom-modal modal-padding"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Adicionar Novo Arquivo</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <div className="mb-3">
                            <label className="col-form-label">
                                Título <span className="text-danger">*</span>
                            </label>
                            <input 
                                className="form-control" 
                                type="text"
                                value={attachmentTitle}
                                onChange={(e) => setAttachmentTitle(e.target.value)}
                                required
                            />
                        </div>
                        <div className="mb-3">
                            <label className="col-form-label">
                                Descrição
                            </label>
                            <textarea 
                                className="form-control" 
                                rows={4}
                                value={attachmentDescription}
                                onChange={(e) => setAttachmentDescription(e.target.value)}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="col-form-label">
                                Arquivo <span className="text-danger">*</span>
                            </label>
                            <div className="drag-attach">
                                <input 
                                    type="file" 
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                    accept="*/*"
                                />
                                <div className="img-upload">
                                    <i className="ti ti-file-broken" />
                                    Enviar Arquivo
                                </div>
                            </div>
                        </div>
                        {uploadedFile && (
                            <div className="mb-3">
                                <label className="col-form-label">Arquivo Enviado</label>
                                <div className="upload-file upload-list">
                                    <div>
                                        <h6>{uploadedFile.name}</h6>
                                        <p>{uploadedFile.size}</p>
                                    </div>
                                    <button 
                                        onClick={handleRemoveFile}
                                        className="text-danger border-0 bg-transparent"
                                        type="button"
                                    >
                                        <i className="ti ti-trash-x" />
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="col-lg-12 text-end modal-btn">
                            <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={handleClose}
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSaveAttachment}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Salvando...
                                    </>
                                ) : (
                                    'Confirmar'
                                )}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* Modal de Sucesso */}
            <Modal 
                show={showSuccessModal} 
                onHide={() => {
                    setShowSuccessModal(false);
                    setShowAttachmentModal(false);
                }}
                centered
            >
                <div className="modal-body">
                    <div className="success-message text-center">
                        <div className="success-popup-icon bg-success-light">
                            <i className="ti ti-check-check fs-36 text-success" />
                        </div>
                        <h3>Arquivo Anexado com Sucesso!</h3>
                        <p>O arquivo foi adicionado à oportunidade.</p>
                        <div className="col-lg-12 text-center modal-btn">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    setShowAttachmentModal(false);
                                }}
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>
        </>
    );
};